import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { DEFAULT_PAGE_SIZE } from '@/__core/constants/pagination';
import { Box, Button, CircularProgress, Menu, MenuItem, Typography } from '@mui/material';
import { ArrowDropDown as ArrowDropDownIcon } from '@mui/icons-material';

import { AppPagination } from '@/__core/components';
import DeleteConfirmationModal from '@/__core/components/DeleteConfirmationModal';
import DataTableModule from '@/__core/layout/module/DataTableModule';
import { sharedListStyles } from '@/__core/styles/sharedListStyles';

import { getStores } from '../redux';
import { formatStoreAddress, InventoryStoreRow } from '../models/StoreModel';

export type StoreListHandle = {
  requestRefresh: () => void;
  requestDelete: () => void;
};

type StoreListPagination = {
  count: number;
  page: number;
  pageSize: number;
  totalPages: number;
};

interface StoreListProps {
  companyId?: string;
  searchQuery?: string;
  status?: string;
  onSelectRow?: (row: InventoryStoreRow) => void;
  onSelectionChange?: (selectedIds: string[]) => void;
  onRowsChange?: (rows: InventoryStoreRow[]) => void;
  onFetchError?: (message: string) => void;
  onBulkDelete?: (storeIds: string[]) => void | Promise<void>;
  onBulkDisable?: (storeIds: string[]) => void | Promise<void>;
  canUpdate?: boolean;
  canDelete?: boolean;
}

const StoreList = forwardRef<StoreListHandle, StoreListProps>(function StoreList(
  {
    companyId,
    searchQuery = '',
    status = 'all',
    onSelectRow,
    onSelectionChange,
    onRowsChange,
    onFetchError,
    onBulkDelete,
    onBulkDisable,
    canUpdate = true,
    canDelete = true,
  },
  ref
) {
  const [rows, setRows] = useState<InventoryStoreRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const [pagination, setPagination] = useState<StoreListPagination>({
    count: 0,
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalPages: 1,
  });
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [actionAnchorEl, setActionAnchorEl] = useState<null | HTMLElement>(null);
  const [applyingAction, setApplyingAction] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const canBulkSelect = canUpdate || canDelete;
  const actionMenuOpen = Boolean(actionAnchorEl);

  useEffect(() => {
    setPagination((current) => ({ ...current, page: 1 }));
  }, [companyId, searchQuery, status]);

  useEffect(() => {
    let active = true;

    const fetchStores = async () => {
      setLoading(true);
      try {
        const response = await getStores({
          search: searchQuery,
          status,
          company_id: companyId,
          page: pagination.page,
          page_size: pagination.pageSize,
        });

        if (!active) return;

        const nextRows = response.results || [];
        setRows(nextRows);
        onRowsChange?.(nextRows);
        setPagination({
          count: response.count || 0,
          page: response.page || pagination.page,
          pageSize: response.page_size || pagination.pageSize,
          totalPages: response.total_pages || 1,
        });
      } catch (error: any) {
        if (!active) return;
        setRows([]);
        onRowsChange?.([]);
        setPagination((current) => ({ ...current, count: 0, totalPages: 1 }));
        onFetchError?.(
          error?.response?.data?.errors?.[0]?.detail || error?.response?.data?.detail || 'Không thể tải danh sách kho.'
        );
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    const timeout = window.setTimeout(() => {
      void fetchStores();
    }, 250);

    return () => {
      active = false;
      window.clearTimeout(timeout);
    };
  }, [companyId, searchQuery, status, pagination.page, pagination.pageSize, reloadTick]);

  useEffect(() => {
    const rowIds = new Set(rows.map((row) => row.id));
    setSelectedIds((current) => current.filter((id) => rowIds.has(id)));
  }, [rows]);

  useEffect(() => {
    onSelectionChange?.(selectedIds);
  }, [onSelectionChange, selectedIds]);

  const openDeleteConfirm = useCallback(() => {
    if (!canDelete || selectedIds.length === 0) {
      return;
    }
    setConfirmDeleteOpen(true);
  }, [canDelete, selectedIds.length]);

  useImperativeHandle(
    ref,
    () => ({
      requestRefresh: () => setReloadTick((current) => current + 1),
      requestDelete: openDeleteConfirm,
    }),
    [openDeleteConfirm]
  );

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    setPagination((current) => ({ ...current, page }));
  };

  const handleToggleRow = (storeId: string) => {
    if (!canBulkSelect) {
      return;
    }
    setSelectedIds((current) =>
      current.includes(storeId) ? current.filter((id) => id !== storeId) : [...current, storeId]
    );
  };

  const handleToggleAll = () => {
    if (!canBulkSelect) {
      return;
    }
    setSelectedIds((current) => {
      const allSelected = rows.length > 0 && current.length === rows.length;
      if (allSelected) {
        return [];
      }
      return rows.map((row) => row.id);
    });
  };

  const handleConfirmDelete = async () => {
    if (!canDelete || selectedIds.length === 0) {
      setConfirmDeleteOpen(false);
      return;
    }

    try {
      setApplyingAction(true);
      await Promise.resolve(onBulkDelete?.([...selectedIds]));
      setSelectedIds([]);
    } finally {
      setApplyingAction(false);
      setConfirmDeleteOpen(false);
    }
  };

  const handleBulkDisable = async () => {
    if (!canUpdate || selectedIds.length === 0) {
      return;
    }

    setActionAnchorEl(null);
    try {
      setApplyingAction(true);
      await Promise.resolve(onBulkDisable?.([...selectedIds]));
      setSelectedIds([]);
    } finally {
      setApplyingAction(false);
    }
  };

  const areAllRowsSelected = useMemo(
    () => rows.length > 0 && selectedIds.length === rows.length,
    [rows.length, selectedIds.length]
  );
  const areSomeRowsSelected = useMemo(
    () => selectedIds.length > 0 && selectedIds.length < rows.length,
    [rows.length, selectedIds.length]
  );

  const totalCount = pagination.count;
  const currentPage = pagination.page;
  const pageSize = pagination.pageSize;
  const totalPages = pagination.totalPages;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 220 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!rows.length) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 1, height: 220 }}>
        <Typography variant='body1' color='text.secondary'>Không có kho nào</Typography>
        <Typography variant='body2' color='text.secondary'>Thử thay đổi bộ lọc hoặc tạo kho mới.</Typography>
      </Box>
    );
  }

  const columns = [
    { id: 'name', label: 'Tên', width: '280px' },
    { id: 'address', label: 'Địa chỉ', width: '420px', render: (_: unknown, row: InventoryStoreRow) => formatStoreAddress(row.address) || '-' },
    {
      id: 'status',
      label: 'Trạng thái',
      width: '160px',
      render: (_: unknown, row: InventoryStoreRow) => (
        <Typography component='span' variant='body2' sx={{ color: row.status_color || 'inherit', fontWeight: 500 }}>
          {row.status_label || '-'}
        </Typography>
      ),
    },
  ];

  return (
    <Box sx={sharedListStyles.container}>
      <DataTableModule
        data={rows}
        columns={columns}
        fullWidth
        leftFilterWidth={0}
        onRowSelect={handleToggleRow}
        onSelectAll={handleToggleAll}
        onRowClick={(row) => onSelectRow?.(row as InventoryStoreRow)}
        getRowId={(row) => (row as InventoryStoreRow).id}
        isRowSelected={(row) => selectedIds.includes((row as InventoryStoreRow).id)}
        areAllRowsSelected={areAllRowsSelected}
        areSomeRowsSelected={areSomeRowsSelected}
        showCheckbox={canBulkSelect}
      />

      {selectedIds.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            px: 2,
            py: 2,
            boxSizing: 'border-box',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography variant='body2' sx={{ color: '#374151' }}>
              Đã chọn {selectedIds.length} kho
            </Typography>
            <Button
              variant='outlined'
              endIcon={<ArrowDropDownIcon />}
              onClick={(event) => setActionAnchorEl(event.currentTarget)}
              disabled={applyingAction}
              sx={{
                borderRadius: 0.5,
                textTransform: 'none',
                borderColor: '#d1d5db',
                color: '#374151',
              }}
            >
              {applyingAction ? 'Đang xử lý...' : 'Thao tác'}
            </Button>
            <Menu
              anchorEl={actionAnchorEl}
              open={actionMenuOpen}
              onClose={() => setActionAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
              {canDelete ? (
                <MenuItem onClick={() => { setActionAnchorEl(null); openDeleteConfirm(); }} disabled={applyingAction}>
                  Xóa
                </MenuItem>
              ) : null}
              {canUpdate ? (
                <MenuItem onClick={() => { void handleBulkDisable(); }} disabled={applyingAction}>
                  Ngừng hoạt động
                </MenuItem>
              ) : null}
            </Menu>
          </Box>
        </Box>
      )}

      <AppPagination
        count={totalCount}
        page={currentPage}
        pageSize={pageSize}
        totalPages={totalPages}
        itemLabel='kho'
        onPageChange={handlePageChange}
      />

      <DeleteConfirmationModal
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={() => { void handleConfirmDelete(); }}
        title='Xác nhận xóa kho'
        itemCount={selectedIds.length}
        itemName='kho'
        isLoading={applyingAction}
        loadingText='Đang xóa kho...'
        confirmText='Xóa kho'
        cancelText='Hủy'
      />
    </Box>
  );
});

export default StoreList;
